// MFASecurityPage.jsx

import * as React from 'react';
import { useState, useEffect } from 'react';
import { 
  Button, 
  Checkbox, 
  FormControlLabel, 
  TextField, 
  Typography, 
  Snackbar, 
  Paper, 
  Container,
  Box,
  CircularProgress
} from '@mui/material';
import Alert from '@mui/material/Alert';

export default function MFASecurityPage({ 
  onVerifyEmail, 
  onVerifyOTP, 
  loading, 
  accountName 
}) {
  const [email, setEmail] = useState('');
  const [otp, setOTP] = useState('');
  const [hasAgreed, setHasAgreed] = useState(false);
  const [open, setOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);

  const handleEmailSubmit = () => {
    if (email.trim() === '') {
      setAlertMsg('Email is required.');
      setOpen(true);
      return;
    }

    if (!hasAgreed) {
      setAlertMsg('You must agree to the terms to proceed.');
      setOpen(true);
      return;
    }

    if (onVerifyEmail) {
      onVerifyEmail(email)
        .then(success => {
          if (success) {
            setShowOTPInput(true);
          }
        })
        .catch(error => {
          setAlertMsg(error.message || 'Failed to verify email. Please try again.');
          setOpen(true);
        });
    }
  };

  const handleOTPSubmit = () => {
    if (otp.trim() === '') {
      setAlertMsg('Verification code is required.');
      setOpen(true);
      return;
    }

    if (onVerifyOTP) {
      onVerifyOTP(email, otp)
        .catch(error => {
          setAlertMsg(error.message || 'Invalid verification code. Please try again.');
          setOpen(true);
        });
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={8} style={{ marginTop: '20px', paddingTop: '20px', paddingBottom: '20px' }}>
        <Typography variant="h5" align="center">
          Secure Access Verification
        </Typography>
        
        {accountName && (
          <Typography 
            variant="h6" 
            align="center" 
            style={{ 
              margin: '15px',
              fontWeight: 'bold',
              color: '#000',
              borderBottom: '1px solid #e0e0e0',
              paddingBottom: '10px'
            }}
          >
            {accountName}
          </Typography>
        )}

        {!showOTPInput ? (
          // Email Input Form
          <form noValidate autoComplete="off" style={{ margin: '20px' }}>
            <Typography variant="body1" style={{ marginBottom: '15px' }}>
              This link requires additional security verification. Please enter your work email address to continue.
            </Typography>
            
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <FormControlLabel
              control={
                <Checkbox
                  value="allowExtraEmails"
                  color="primary"
                  checked={hasAgreed}
                  onChange={(e) => setHasAgreed(e.target.checked)}
                />
              }
              label={
                <div style={{
                  border: '2px solid #D32F2F',
                  borderRadius: '5px',
                  padding: '10px',
                  backgroundColor: '#ffebee',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  margin: '10px 0'
                }}>
                  <Typography variant="body2" style={{
                    fontWeight: 'bold'
                  }}>
                    Under penalty of law, I confirm that I am authorized to use this system.
                  </Typography>
                </div>
              }
            />
            
            <Button
              type="button"
              fullWidth
              variant="contained"
              color="primary"
              style={{ margin: '20px 0' }}
              onClick={handleEmailSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Verification Code'}
            </Button>
          </form>
        ) : (
          // OTP Input Form
          <form noValidate autoComplete="off" style={{ margin: '20px' }}>
            <Typography variant="body1" style={{ marginBottom: '15px' }}>
              A verification code has been sent to <strong>{email}</strong>. 
              Please enter the code below to continue.
            </Typography>
            
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="otp"
              label="Verification Code"
              name="otp"
              autoFocus
              value={otp}
              onChange={(e) => setOTP(e.target.value)}
            />
            
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                onClick={() => setShowOTPInput(false)}
              >
                Back
              </Button>
              
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleOTPSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify Code'}
              </Button>
            </Box>
            
            <Box mt={2} textAlign="center">
              <Button
                type="button"
                color="primary"
                onClick={() => onVerifyEmail(email)}
                disabled={loading}
              >
                Resend Code
              </Button>
            </Box>
          </form>
        )}

        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleClose} severity="warning">
            {alertMsg}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}